import React, { memo, useState, useCallback } from 'react';
import { Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';

import ContentHeader from 'modules/views/shared/Wizard/ContentHeader';
import WithWizard from 'modules/views/shared/Wizard/WithWizard';
import NavigationButtons from 'modules/views/shared/NavigationButtons';
import SkippableFieldsModal from 'modules/views/shared/SkippableFieldsModal';
import { TradesSection, AddOtherTradeButton } from './components';
import { getSkippedFields } from 'utils/skippableFieldsUtils';

import { useTrades } from './hooks';
import { useStyles } from './styles';

const Trades: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  // State for skippable fields modal
  const [showSkippableFieldsModal, setShowSkippableFieldsModal] = useState(false);

  // All trades functionality using centralized hook
  const {
    isLoading,
    values,
    errors,
    roleFields,
    tradeFields,
    otherTradeFields,
    isRoleFieldsSectionVisible,
    isTradeFieldsSectionVisible,
    isOtherTradeSelected,
    otherTradesId,
    handleBack,
    handleNext: originalHandleNext,
    handleToggleOtherTrade,
    setValue,
    canGoToPreviousStep,
    getNextStepName,
    fieldDisplayNames,
    getDynamicFormFieldMap,
    profileFieldConstraintsMap,
    hasReachedConsentForm,
    isArchivedProject,
  } = useTrades();

  // Normalize trades values for the skippable fields check.
  // The "Other" sentinel ID is added to trades[] when the toggle is clicked, but it is not
  // a real trade selection — it only means the user opened the Other section. We strip it
  // and instead count otherTradesList entries so the emptiness check is semantically correct.
  const getValuesForSkipCheck = useCallback(
    (formValues: typeof values) => {
      const realMasterTrades = (formValues.trades || []).filter(id => id !== otherTradesId);
      const otherTradeProxies = (formValues.otherTradesList || []).map(t => t.OtherText);
      return { ...formValues, trades: [...realMasterTrades, ...otherTradeProxies] };
    },
    [otherTradesId],
  );

  // Handle when user chooses to continue with skipped fields
  const handleContinueWithSkippedFields = useCallback(async () => {
    setShowSkippableFieldsModal(false);
    await originalHandleNext();
  }, [originalHandleNext]);

  // Handle form submission with skippable fields check
  const handleNext = useCallback(async () => {
    // Check for skippable fields:
    // - Before consent form: All skippable fields trigger warning
    // - After consent form: Only Badge Printing/Activation fields trigger warning
    const dynamicFormFieldMap = getDynamicFormFieldMap(values);
    const skippedFields = getSkippedFields(
      getValuesForSkipCheck(values),
      profileFieldConstraintsMap,
      hasReachedConsentForm,
      fieldDisplayNames,
      dynamicFormFieldMap,
    );

    if (skippedFields.length > 0) {
      setShowSkippableFieldsModal(true);
      return;
    }

    // Proceed with normal form submission
    await originalHandleNext();
  }, [
    hasReachedConsentForm,
    getDynamicFormFieldMap,
    getValuesForSkipCheck,
    values,
    profileFieldConstraintsMap,
    fieldDisplayNames,
    originalHandleNext,
  ]);

  // Calculate skipped fields for the modal
  const skippedFields = showSkippableFieldsModal
    ? getSkippedFields(
        getValuesForSkipCheck(values),
        profileFieldConstraintsMap,
        hasReachedConsentForm,
        fieldDisplayNames,
        getDynamicFormFieldMap(values),
      )
    : [];

  // Main content render
  const content = (
    <div className={classes.container}>
      <ContentHeader title={t('trades.title')} subtitle={t('trades.subtitle')} styleClass={classes.header} />

      {/* Role and Basic Information Section */}
      {isRoleFieldsSectionVisible && (
        <>
          <TradesSection
            fields={roleFields}
            values={values}
            errors={errors}
            setValue={setValue}
            gridSpacing={3}
            defaultGridSize={6}
            isArchivedProject={isArchivedProject}
          />
          {isTradeFieldsSectionVisible && <Divider className={classes.divider} />}
        </>
      )}

      {isTradeFieldsSectionVisible && (
        <>
          {/* Trades Selection Section */}
          <TradesSection
            fields={tradeFields}
            values={values}
            errors={errors}
            setValue={setValue}
            title={t('trades.selectedTrades')}
            required={tradeFields[0]?.isRequired}
            gridSpacing={3}
            defaultGridSize={12}
            isArchivedProject={isArchivedProject}
          />

          {/* Add Other Trade Toggle Button */}
          {!isArchivedProject && (
            <AddOtherTradeButton
              isOtherTradeSelected={isOtherTradeSelected}
              onToggle={handleToggleOtherTrade}
              className={classes.addTradeButton}
            />
          )}

          {/* Other Trades Section (Conditional) */}
          {isOtherTradeSelected && (
            <TradesSection
              fields={otherTradeFields}
              values={values}
              errors={errors}
              setValue={setValue}
              gridSpacing={3}
              defaultGridSize={6}
              isArchivedProject={isArchivedProject}
            />
          )}
        </>
      )}

      {/* Navigation Buttons */}
      <NavigationButtons
        secondaryButtonProps={{
          isHidden: !canGoToPreviousStep,
          onClick: handleBack,
          testId: 'trades-back-button',
        }}
        primaryButtonProps={{
          label: getNextStepName() ? `${t('common.next')}: ${getNextStepName()}` : t('trades.next'),
          isLoading,
          hasEndArrow: true,
          styleClass: classes.nextButton,
          testId: 'trades-next-button',
          onClick: handleNext,
        }}
      />

      {/* Skippable Fields Modal */}
      {showSkippableFieldsModal && (
        <SkippableFieldsModal
          isVisible={showSkippableFieldsModal}
          skippedFields={skippedFields}
          isLoading={isLoading}
          onContinue={handleContinueWithSkippedFields}
          onFillFields={() => setShowSkippableFieldsModal(false)}
          onClose={() => setShowSkippableFieldsModal(false)}
        />
      )}
    </div>
  );

  // Only pass visible fields to WithWizard for sidebar display
  const visibleFields = roleFields
    .concat(tradeFields)
    .filter(field => field.isVisible)
    .map(field => ({
      name: field.name,
      id: field.id,
      isRequired: field.isRequired,
      isSkippable: field.isSkippable,
      skippableTill: field.skippableTill,
    }));

  return <WithWizard disableNavigation={isLoading} fields={visibleFields} content={content} />;
};

export default memo(Trades);
